import { useEffect, useRef, useState } from "react";
import { FileText, Check, Loader2, ChevronDown } from "lucide-react";
import { carregarTermosVigentes, modulosDaFicha, MODULO_LABEL } from "../lib/termos";

// Aceite click-wrap: mostra o texto INTEGRAL de cada termo numa caixa com rolagem
// e só libera o checkbox depois que a pessoa rola até o fim (scroll-gate). Um
// checkbox por módulo (Manutenção, Combustível). É o que dá lastro ao aceite:
// não dá para marcar sem ter tido o texto à frente.
//
// Se os termos não carregarem (rede) ou a ficha não exigir termo, cai num
// checkbox único simples — o cadastro nunca fica refém do termo ter baixado.
//
// Reporta ao pai por onChange({ pronto, aceites }):
//   pronto  = todos os termos exigidos foram aceitos
//   aceites = [{ termo_versao_id, modulo, codigo, versao, hash_sha256 }]

export default function TermosAceite({ sistemas, onChange, accent = "#7c3aed" }) {
  const [termos, setTermos] = useState(null); // null = carregando; [] = sem termo/falha
  const [scrolled, setScrolled] = useState({}); // modulo -> chegou ao fim
  const [checked, setChecked] = useState({});   // modulo -> marcado
  const boxRefs = useRef({});

  const mods = modulosDaFicha(sistemas);
  const modsKey = mods.join(",");

  useEffect(() => {
    let vivo = true;
    setTermos(null);
    setScrolled({});
    setChecked({});
    carregarTermosVigentes(mods).then((t) => { if (vivo) setTermos(t); });
    return () => { vivo = false; };
  }, [modsKey]); // eslint-disable-line

  // Reporta prontidão ao pai. Fallback (termos vazio) usa a chave "_generico".
  useEffect(() => {
    if (termos === null) { onChange?.({ pronto: false, aceites: [] }); return; }
    if (!termos.length) { onChange?.({ pronto: !!checked._generico, aceites: [] }); return; }
    const pronto = termos.every((t) => checked[t.modulo]);
    const aceites = termos.map((t) => ({
      termo_versao_id: t.id, modulo: t.modulo, codigo: t.codigo, versao: t.versao, hash_sha256: t.hash_sha256,
    }));
    onChange?.({ pronto, aceites });
  }, [termos, checked]); // eslint-disable-line

  const onScrollBox = (modulo) => (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolled((p) => (p[modulo] ? p : { ...p, [modulo]: true }));
    }
  };

  // Termo curto que já cabe na caixa sem rolar: libera o checkbox de imediato.
  useEffect(() => {
    if (!termos?.length) return;
    const t = setTimeout(() => {
      const upd = {};
      for (const term of termos) {
        const el = boxRefs.current[term.modulo];
        if (el && el.scrollHeight <= el.clientHeight + 24) upd[term.modulo] = true;
      }
      if (Object.keys(upd).length) setScrolled((p) => ({ ...upd, ...p }));
    }, 60);
    return () => clearTimeout(t);
  }, [termos]);

  if (termos === null) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px", fontSize: 12.5, color: "#64748b" }}>
        <Loader2 size={15} className="spin" /> Carregando os termos para leitura...
      </div>
    );
  }

  // ── Fallback: sem termo versionado (só rastreamento, ou falha de rede) ──
  if (!termos.length) {
    const on = !!checked._generico;
    return (
      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", background: on ? "#f0fdf4" : "#f8fafc", border: `1px solid ${on ? "#bbf7d0" : "#e2e8f0"}`, borderRadius: 10, padding: "12px 14px" }}>
        <input type="checkbox" checked={on} onChange={(e) => setChecked({ _generico: e.target.checked })} style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0 }} />
        <span style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.45 }}>
          <strong>Li e aceito os termos de credenciamento</strong> da rede InstaSolutions e declaro que as informações e documentos enviados são verdadeiros. Este aceite eletrônico dispensa assinatura manual.
        </span>
      </label>
    );
  }

  // ── Click-wrap: um termo por módulo, com scroll-gate ──
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {termos.map((t) => {
        const podeMarcar = !!scrolled[t.modulo];
        const on = !!checked[t.modulo];
        return (
          <div key={t.modulo} style={{ border: `1px solid ${on ? "#bbf7d0" : "#e2e8f0"}`, borderRadius: 12, overflow: "hidden", background: on ? "#f0fdf4" : "#fff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: on ? "#ecfdf5" : "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <FileText size={15} color={accent} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Termo de Uso — {MODULO_LABEL[t.modulo] || t.modulo}</div>
                <div style={{ fontSize: 10.5, color: "#94a3b8" }}>{t.codigo} · versão {t.versao}</div>
              </div>
            </div>

            <div
              ref={(el) => { boxRefs.current[t.modulo] = el; }}
              onScroll={onScrollBox(t.modulo)}
              style={{ maxHeight: 220, overflowY: "auto", padding: "12px 14px", fontSize: 11.5, lineHeight: 1.55, color: "#334155", whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#fff" }}
            >
              {t.conteudo_md}
            </div>

            {!podeMarcar && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px", fontSize: 11, color: "#b45309", background: "#fffbeb", borderTop: "1px solid #fde68a" }}>
                <ChevronDown size={13} /> Role o texto até o fim para liberar o aceite
              </div>
            )}

            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "11px 14px", borderTop: "1px solid #e2e8f0", cursor: podeMarcar ? "pointer" : "not-allowed", opacity: podeMarcar ? 1 : 0.55 }}>
              <input
                type="checkbox"
                disabled={!podeMarcar}
                checked={on}
                onChange={(e) => setChecked((p) => ({ ...p, [t.modulo]: e.target.checked }))}
                style={{ marginTop: 2, width: 18, height: 18, flexShrink: 0 }}
              />
              <span style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.45 }}>
                <strong>Li e aceito</strong> o Termo de Uso de {MODULO_LABEL[t.modulo] || t.modulo} ({t.codigo}, versão {t.versao}) e declaro que as informações e documentos enviados são verdadeiros. Este aceite eletrônico dispensa assinatura manual.
              </span>
              {on && <Check size={16} color="#059669" style={{ flexShrink: 0, marginTop: 1 }} />}
            </label>
          </div>
        );
      })}
    </div>
  );
}
